/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type UserInfo } from '@coze-foundation/account-base';

import {
  getLoginAccountInfo,
  postUcBridgeViaBff,
  postUcLoginViaBff,
} from './uc-bff-api';
import { SESSION_USERKEY, SESSION_USER_INFO } from './constants';
import {
  readAuthorizationFromCookie,
  readTenantIdFromCookie,
} from './authorization-cookie';
import { resolveTenantIdFromUserInfo } from './auth-headers';

let ensureUcAccountInfoPromise: Promise<string | undefined> | null = null;

/** 解析 `/uc/login/pwd` 返回的 info（字符串 userkey 或含 token 字段的对象） */
export function tokenFromLoginInfo(info: unknown): string | undefined {
  if (typeof info === 'string' && info.trim() !== '') {
    return info.trim();
  }
  if (info !== null && info !== undefined && typeof info === 'object') {
    const { token } = info as Record<string, unknown>;
    if (typeof token === 'string' && token.trim() !== '') {
      return token.trim();
    }
  }
  return undefined;
}

export function setSessionUserkey(userkey: string): void {
  sessionStorage.setItem(SESSION_USERKEY, userkey);
}

export function getSessionUserkey(): string {
  return sessionStorage.getItem(SESSION_USERKEY)?.trim() || '';
}

/** UC 登录态以 Authorization Cookie 为准（后端 SessionAuthMW 读取） */
export function hasUcSession(): boolean {
  return !!readAuthorizationFromCookie().value;
}

/** @deprecated 改用 Cookie 鉴权 */
export function restoreSessionUserkeyFromCookie(): boolean {
  return hasUcSession();
}

function normalizeUserInfoForSession(raw: unknown): Record<string, unknown> {
  const userInfo =
    raw !== null &&
    raw !== undefined &&
    typeof raw === 'object' &&
    !Array.isArray(raw)
      ? { ...(raw as Record<string, unknown>) }
      : {};
  const tenantId = resolveTenantIdFromUserInfo(userInfo);
  if (tenantId) {
    userInfo.tenantId = tenantId;
  }
  return userInfo;
}

export async function fetchLoginAccountInfo(): Promise<
  Record<string, unknown>
> {
  const info = await getLoginAccountInfo();
  const normalized = normalizeUserInfoForSession(info ?? {});
  sessionStorage.setItem(SESSION_USER_INFO, JSON.stringify(normalized));
  return normalized;
}

/** 从 sessionStorage.userInfo 或 TenantId Cookie 同步解析 UC 租户 ID */
export function resolveUcTenantIdFromStorage(): string | undefined {
  try {
    const raw = sessionStorage.getItem(SESSION_USER_INFO);
    if (raw) {
      const userInfo = JSON.parse(raw) as Record<string, unknown>;
      const tenantId = resolveTenantIdFromUserInfo(userInfo);
      if (tenantId) {
        return tenantId;
      }
    }
  } catch (error) {
    void error;
  }
  return readTenantIdFromCookie();
}

/**
 * 已有 Authorization Cookie 但 session 未缓存 tenant 时，拉取 loginAccountInfo 并写入 sessionStorage。
 */
export async function ensureUcAccountInfoLoaded(): Promise<string | undefined> {
  const cached = resolveUcTenantIdFromStorage();
  if (cached) {
    return cached;
  }
  if (!hasUcSession()) {
    return undefined;
  }
  if (!ensureUcAccountInfoPromise) {
    ensureUcAccountInfoPromise = fetchLoginAccountInfo()
      .then(info => resolveTenantIdFromUserInfo(info))
      .catch(() => undefined)
      .finally(() => {
        ensureUcAccountInfoPromise = null;
      });
  }
  return ensureUcAccountInfoPromise;
}

function persistTenantAfterLogin(tenantId?: string): void {
  if (!tenantId) {
    return;
  }
  sessionStorage.setItem(
    SESSION_USER_INFO,
    JSON.stringify(normalizeUserInfoForSession({ tenantId })),
  );
}

function assertPassportUserInfo(userInfo?: UserInfo): UserInfo {
  if (!userInfo?.user_id_str) {
    throw new Error('登录成功但未返回有效用户信息');
  }
  return userInfo;
}

/** 通过 Coze 后端统一桥接 UC 登录 */
export async function loginViaCozeBackend(params: {
  account: string;
  password: string;
}): Promise<UserInfo> {
  sessionStorage.removeItem(SESSION_USERKEY);
  sessionStorage.removeItem(SESSION_USER_INFO);

  const { data: userInfo, uc_tenant_id: tenantId } =
    await postUcLoginViaBff(params);
  const validUser = assertPassportUserInfo(userInfo);
  persistTenantAfterLogin(tenantId);
  await fetchLoginAccountInfo().catch(() => undefined);
  return validUser;
}

/** 页面刷新时 Coze session 失效，用已有 userkey 重新桥接（内部接口，正常登录不走） */
export async function bridgeCozeSession(): Promise<UserInfo> {
  const { data: userInfo, uc_tenant_id: tenantId } =
    await postUcBridgeViaBff();
  const validUser = assertPassportUserInfo(userInfo);
  if (tenantId) {
    persistTenantAfterLogin(tenantId);
  } else {
    await fetchLoginAccountInfo().catch(() => undefined);
  }
  return validUser;
}
